import React, { useState } from 'react';
import { 
  PresentationChartBarIcon, 
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  PlayIcon,
  PauseIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../../features/theme/context/ThemeContext';

const AIPresentationCard = ({ presentation, messageId, onRefresh }) => {
  const { darkMode } = useTheme();
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSlideshow, setIsSlideshow] = useState(false);
  
  if (!presentation) return null;

  const { status, title, originalPrompt, errorMessage, slides, totalSlides } = presentation;

  const currentSlide = slides?.[currentSlideIndex];

  const getStatusIcon = () => {
    switch (status) {
      case 'planning':
        return <ArrowPathIcon className="h-5 w-5 animate-spin text-blue-500" />;
      case 'generating':
        return <ArrowPathIcon className="h-5 w-5 animate-spin text-blue-500" />;
      case 'completed':
        return <PresentationChartBarIcon className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <PresentationChartBarIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'planning':
        return 'Planning presentation...';
      case 'generating':
        return 'Generating slides...';
      case 'completed':
        return 'AI Presentation ready';
      case 'failed':
        return 'Generation failed';
      default:
        return 'Unknown status';
    }
  };

  const getBorderColor = () => {
    switch (status) {
      case 'planning':
      case 'generating':
        return 'border-blue-200 dark:border-blue-800';
      case 'completed':
        return 'border-green-200 dark:border-green-800';
      case 'failed':
        return 'border-red-200 dark:border-red-800';
      default:
        return 'border-gray-200 dark:border-gray-700';
    }
  };

  const getBgColor = () => {
    switch (status) {
      case 'planning':
      case 'generating':
        return 'bg-blue-50 dark:bg-blue-950/30';
      case 'completed':
        return 'bg-green-50 dark:bg-green-950/30';
      case 'failed':
        return 'bg-red-50 dark:bg-red-950/30';
      default:
        return 'bg-gray-50 dark:bg-gray-900/30';
    }
  };

  const nextSlide = () => {
    if (currentSlideIndex < (totalSlides || slides?.length || 0) - 1) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlideIndex(index);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    if (!isFullscreen) {
      // Reset to first slide when entering fullscreen
      setCurrentSlideIndex(0);
    }
  };

  const toggleSlideshow = () => {
    setIsSlideshow(!isSlideshow);
    // TODO: Implement auto-advance slides when slideshow is active
  };

  // Fullscreen modal
  if (isFullscreen && status === 'completed' && slides?.length) {
    return (
      <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
        {/* Controls */}
        <div className="absolute top-4 right-4 flex gap-2 z-10">
          <button
            onClick={toggleSlideshow}
            className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg"
            title={isSlideshow ? "Pause slideshow" : "Start slideshow"}
          >
            {isSlideshow ? <PauseIcon className="h-5 w-5" /> : <PlayIcon className="h-5 w-5" />}
          </button>
          <button
            onClick={toggleFullscreen}
            className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg"
            title="Exit fullscreen"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <button
          onClick={prevSlide}
          disabled={currentSlideIndex === 0}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 disabled:opacity-30 text-white rounded-lg z-10"
        >
          <ChevronLeftIcon className="h-6 w-6" />
        </button>

        <button
          onClick={nextSlide}
          disabled={currentSlideIndex >= slides.length - 1}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 p-2 bg-black/50 hover:bg-black/70 disabled:opacity-30 text-white rounded-lg z-10"
        >
          <ChevronRightIcon className="h-6 w-6" />
        </button>

        {/* Slide content */}
        <div 
          className="w-full h-full"
          dangerouslySetInnerHTML={{ __html: currentSlide?.html }}
        />

        {/* Slide indicator */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 px-3 py-1 rounded-lg text-white text-sm">
          {currentSlideIndex + 1} / {slides.length}
        </div>
      </div>
    );
  }

  return (
    <div className={`max-w-4xl rounded-2xl border-2 ${getBorderColor()} ${getBgColor()} p-4 transition-all duration-200`}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        {getStatusIcon()}
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
            {getStatusText()}
          </h3>
          {title && (
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-1">
              {title}
            </p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="mb-4">
        {(status === 'planning' || status === 'generating') && (
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
            </div>
            <span>
              {status === 'planning' ? 'Planning your presentation structure...' : 'Generating slide content...'}
            </span>
          </div>
        )}

        {status === 'completed' && slides?.length && (
          <div>
            <div className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              <p className="font-medium mb-1">✅ Your AI presentation is ready!</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {totalSlides || slides.length} slides • Based on: "{originalPrompt?.substring(0, 60)}{originalPrompt?.length > 60 ? '...' : ''}"
              </p>
            </div>

            {/* Slide preview */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 mb-4 border">
              <div className="aspect-video bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden relative">
                {currentSlide?.html ? (
                  <div 
                    className="absolute inset-0 flex items-center justify-center"
                    style={{ 
                      width: '100%',
                      height: '100%'
                    }}
                  >
                    <div
                      style={{ 
                        width: '1280px',
                        height: '1280px',
                        transform: 'scale(0.6)',
                        transformOrigin: 'center center'
                      }}
                      dangerouslySetInnerHTML={{ __html: currentSlide.html }}
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <PresentationChartBarIcon className="h-12 w-12" />
                  </div>
                )}
              </div>
              
              {/* Slide info */}
              <div className="mt-2 flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-sm text-gray-800 dark:text-gray-200">
                    {currentSlide?.title || `Slide ${currentSlideIndex + 1}`}
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {currentSlide?.type} • {currentSlide?.keyPoints?.length || 0} key points
                  </p>
                </div>
                <span className="text-xs text-gray-500">
                  {currentSlideIndex + 1} / {slides.length}
                </span>
              </div>
            </div>

            {/* Navigation controls */}
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={prevSlide}
                disabled={currentSlideIndex === 0}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              
              <div className="flex-1 flex justify-center gap-1">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === currentSlideIndex 
                        ? 'bg-blue-500' 
                        : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                    }`}
                  />
                ))}
              </div>
              
              <button
                onClick={nextSlide}
                disabled={currentSlideIndex >= slides.length - 1}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2">
              <button
                onClick={toggleFullscreen}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
              >
                <PresentationChartBarIcon className="h-4 w-4" />
                Present
              </button>
            </div>
          </div>
        )}

        {status === 'failed' && (
          <div className="text-sm text-red-700 dark:text-red-300">
            <p className="font-medium mb-1">❌ Generation failed</p>
            {errorMessage && (
              <p className="text-xs text-red-600 dark:text-red-400">
                {errorMessage}
              </p>
            )}
            {onRefresh && (
              <button
                onClick={() => onRefresh(originalPrompt)}
                className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline mt-1"
              >
                Try again
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIPresentationCard; 